'use client';
import React from 'react';
import { Flag, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';

interface Question {
  id: number;
  passage: number;
  type: string;
  typeLabel: string;
  text: string;
  options?: string[];
  answer: string;
  wordLimit?: number;
}

const TYPE_COLORS: Record<string, string> = {
  'TF/NG': 'bg-info-bg text-info border-info/30',
  'YN/NG': 'bg-secondary text-primary border-primary/30',
  'MCQ': 'bg-warning-bg text-warning border-warning/30',
  'MATCH_HEAD': 'bg-danger-bg text-danger border-danger/30',
  'MATCH_INFO': 'bg-success-bg text-accent border-accent/30',
  'MATCH_FEAT': 'bg-purple-50 text-purple-700 border-purple-200',
  'MATCH_END': 'bg-orange-50 text-orange-700 border-orange-200',
  'COMPLETION': 'bg-muted text-foreground border-border',
  'SHORT': 'bg-muted text-foreground border-border',
};

interface QuestionPanelProps {
  question: Question;
  answer: string;
  flagged: boolean;
  onAnswer: (val: string) => void;
  onFlag: () => void;
  onPrev: () => void;
  onNext: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export default function QuestionPanel({
  question, answer, flagged, onAnswer, onFlag, onPrev, onNext, isFirst, isLast,
}: QuestionPanelProps) {
  const typeColor = TYPE_COLORS[question.type] ?? 'bg-muted text-foreground border-border';

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-shrink-0 px-5 py-3 bg-card border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-bold text-foreground">Question {question.id}</span>
          <span className={`badge-qtype border ${typeColor}`}>{question.typeLabel}</span>
          <span className="text-xs text-muted-foreground">Passage {question.passage}</span>
        </div>
        <button
          onClick={onFlag}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-150 ${
            flagged
              ? 'bg-warning-bg text-warning border-warning/40' :'bg-muted text-muted-foreground border-border hover:border-warning/40 hover:text-warning'
          }`}
        >
          <Flag size={12} className={flagged ? 'fill-warning' : ''} />
          {flagged ? 'Flagged' : 'Flag'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin p-5">
        <p className="text-sm font-medium text-foreground leading-relaxed mb-5">{question.text}</p>

        {/* Radio options (TF/NG, YN/NG, MCQ) */}
        {question.options && (question.type === 'TF/NG' || question.type === 'YN/NG') && (
          <div className="flex gap-3">
            {question.options.map((opt) => (
              <button
                key={`opt-${question.id}-${opt}`}
                onClick={() => onAnswer(opt)}
                className={`flex-1 py-3 rounded-xl border-2 text-sm font-semibold transition-all duration-150 ${
                  answer === opt
                    ? 'border-primary bg-secondary text-primary' :'border-border bg-card text-foreground hover:border-primary/40'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        )}

        {question.options && question.type === 'MCQ' && (
          <div className="space-y-2.5">
            {question.options.map((opt) => {
              const letter = opt.charAt(0);
              const isSelected = answer === letter;
              return (
                <button
                  key={`opt-${question.id}-${letter}`}
                  onClick={() => onAnswer(letter)}
                  className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left text-sm transition-all duration-150 ${
                    isSelected
                      ? 'border-primary bg-secondary text-primary' :'border-border bg-card text-foreground hover:border-primary/30 hover:bg-muted/40'
                  }`}
                >
                  <span className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold border-2 mt-0.5 ${
                    isSelected ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'
                  }`}>
                    {letter}
                  </span>
                  <span className="leading-relaxed">{opt.slice(3)}</span>
                </button>
              );
            })}
          </div>
        )}

        {question.options && (question.type === 'MATCH_HEAD' || question.type === 'MATCH_INFO' || question.type === 'MATCH_FEAT' || question.type === 'MATCH_END') && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground mb-3">Select the matching option:</p>
            {question.options.map((opt) => {
              const isSelected = answer === opt;
              return (
                <button
                  key={`opt-${question.id}-${opt.slice(0, 20)}`}
                  onClick={() => onAnswer(opt)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 text-left text-xs transition-all duration-150 ${
                    isSelected
                      ? 'border-primary bg-secondary text-primary' :'border-border bg-card text-foreground hover:border-primary/30'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold border-2 mt-0.5 ${
                    isSelected ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'
                  }`}>
                    {opt.charAt(0)}
                  </span>
                  <span className="leading-relaxed">{opt.slice(2)}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Text input (COMPLETION, SHORT) */}
        {(question.type === 'COMPLETION' || question.type === 'SHORT') && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={13} className="text-warning" />
              <p className="text-xs text-warning font-medium">
                Write no more than {question.wordLimit} word{(question.wordLimit ?? 1) > 1 ? 's' : ''} — copy spelling directly from the passage
              </p>
            </div>
            <input
              type="text"
              value={answer}
              onChange={(e) => onAnswer(e.target.value)}
              placeholder={`Your answer (max ${question.wordLimit} word${(question.wordLimit ?? 1) > 1 ? 's' : ''})`}
              className="input-field text-sm"
            />
            <div className="flex items-center justify-between mt-1.5">
              <p className="text-xs text-muted-foreground">
                Word count: <span className={`font-semibold ${answer.trim().split(/\s+/).filter(Boolean).length > (question.wordLimit ?? 1) ? 'text-danger' : 'text-foreground'}`}>
                  {answer.trim() === '' ? 0 : answer.trim().split(/\s+/).filter(Boolean).length}
                </span> / {question.wordLimit}
              </p>
              {answer && <p className="text-xs text-accent">✓ Answered</p>}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-shrink-0 px-5 py-3 border-t border-border flex items-center justify-between bg-card">
        <button
          onClick={onPrev}
          disabled={isFirst}
          className="btn-outline py-2 px-4 text-xs flex items-center gap-1.5 disabled:opacity-40"
        >
          <ChevronLeft size={14} />
          Previous
        </button>
        <span className="text-xs text-muted-foreground tabular-nums">
          {question.id} of 40
        </span>
        <button
          onClick={onNext}
          disabled={isLast}
          className="btn-primary py-2 px-4 text-xs flex items-center gap-1.5 disabled:opacity-40"
        >
          Next
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}